import * as React from "react";
import { useEffect, useState } from "react";
import { LinearProgress } from "@material-ui/core";
import { useAsync } from "react-use";
import { IFileModelDTO } from "groopit-js/client";
//
// A FileItem either has been added by the user (a File) or is a string (really a Guid)
// which represents a file that has been uploaded to the server and is now being edited
//
type FileItem = File | IFileModelDTO;
interface PreviewProps {
  file: File;
  inputPrefix: string;
}
const FilePreview = (props: PreviewProps) => {
  const { file, inputPrefix } = { ...props };
  const [url, setUrl] = useState<string>();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    //
    // disable the submit button until the pictures is fully uploaded
    //
    const submit = document.querySelector("input.btn-progress");
    if (submit) {
      const refcount = parseInt(submit.getAttribute("disabled") ?? "0");
      submit.setAttribute("disabled", (refcount + 1).toString());
      setUrl(URL.createObjectURL(file));
      return () => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      };
    }
  }, [file]);

  const pending = useAsync(async () => {
    const m = await import("groopit-js");
    const client = new m.GroopItApiClient({});
    const { upload, image } = await client.createPendingUpload(true);
    const azure = await import("@azure/storage-blob");
    const uploader = new azure.BlockBlobClient(upload);

    //
    // We set progress to a max of 99% so that we don't see the progress bar
    // 'stuck' at 100% for a period of time.
    //
    await uploader.uploadData(file, {
      onProgress: (e) => {
        setProgress((99 * e.loadedBytes) / file.size);
      },
      blobHTTPHeaders: { blobContentType: file.type },
    });
    setProgress(99);

    //
    // re-enable the submit button
    //
    const submit = document.querySelector("input.btn-progress");
    if (submit) {
      const refcount = parseInt(submit.getAttribute("disabled") ?? "0");
      if (refcount !== 1) {
        submit.setAttribute("disabled", (refcount - 1).toString());
      } else {
        submit.removeAttribute("disabled");
      }
    }
    return { image };
  }, [file]);

  return (
    <div className="file-component flex-grow">
      <div className={`${pending.loading ? "opacity-50" : ""}`}>
        {file.name}
      </div>
      {pending.loading || !pending.value ? (
        <LinearProgress variant="determinate" value={progress} />
      ) : (
        <>
          <input
            type="hidden"
            name={`${inputPrefix}FileId`}
            value={pending.value.image}
          />
          <input
            type="hidden"
            name={`${inputPrefix}Filename`}
            value={file.name}
          />
        </>
      )}
    </div>
  );
};

interface ExistingFileProps {
  fileModel: IFileModelDTO;
  sequence: number;
  inputPrefix: string;
}
const ExistingFile = (props: ExistingFileProps) => {
  const { fileModel, sequence, inputPrefix } = {
    ...props,
  };

  return (
    <div className="file-component flex-grow">
      <div>{fileModel.filename}</div>
      <input
        type="hidden"
        name={`${inputPrefix}FileId`}
        value={fileModel.fileId}
      />
      <input
        type="hidden"
        name={`${inputPrefix}Filename`}
        value={fileModel.filename}
      />
      <input type="hidden" name={`${inputPrefix}Sequence`} value={sequence} />
    </div>
  );
};

interface FileWrapperProps {
  file: FileItem;
  sequence: number;
  inputPrefix: string;
  onRemove: () => void;
}
//
// common chrome for  handling both
// existing files and new files
//
const FileWrapper = (props: FileWrapperProps) => {
  const { file, sequence, inputPrefix, onRemove } = {
    ...props,
  };
  return (
    <div>
      <div className="flex flex-row-reverse items-center m-4">
        <button
          type="button"
          title="remove image"
          className="m-2"
          onClick={onRemove}
        >
          <img
            title="Remove"
            src="/Content/Images/delete.png"
            className="w-4 max-w-none"
          />
        </button>
        {file instanceof File ? (
          <FilePreview
            file={file}
            inputPrefix={`${inputPrefix}[${sequence}].`}
          />
        ) : (
          <ExistingFile
            fileModel={file}
            sequence={sequence}
            inputPrefix={`${inputPrefix}[${sequence}].`}
          />
        )}
      </div>
    </div>
  );
};

interface Props {
  description: string;
  inputPrefix: string;
  files: IFileModelDTO[];
  disabled: boolean;
}
export const FileList = (props: Props): JSX.Element => {
  const { description, files: fileList } = { ...props };
  const [files, setFiles] = useState<FileItem[]>(fileList);
  return (
    <>
      <label className="text-display-normal text-color-secondary w-full">
        <div className="flex items-center space-x-3 point-on-hover">
          <span className="flex-grow ">{description}</span>
          <img
            title="Add Files"
            src="/Content/Images/add.png"
            className="w-4 point-on-hover"
          />
          <input
            title="Add File"
            type="file"
            className="hidden"
            disabled={props.disabled}
            accept="*"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              setFiles((old) => old.concat(files));
            }}
          />
        </div>
      </label>
      <div>
        {files.map((p, i) => (
          <FileWrapper
            key={p instanceof File ? p.name : p.filename}
            file={p}
            sequence={i}
            onRemove={() => {
              setFiles(files.filter((f) => f !== p));
            }}
            {...props}
          />
        ))}
      </div>
    </>
  );
};
